import { type SanityImageFragment } from '@data/sanity/queries/types/image'
import {
  type SanityProductCartPhoto,
  type SanityProductGalleryPhoto,
  type SanityProductListingPhoto,
  type SanityProductVariantFragment,
  type SanityProductVariantOption,
} from '@data/sanity/queries/types/product'
import { isNumeric, matchesForOption } from './helpers'

/**
 * Gets index from filename (e.g., "title_option_1.jpg" => "1").
 */
export const getIndexFormFilename = (filename: string) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  const filenameIndexMap: Record<string, number> = {
    F: 1,
    B: 2,
  }

  const filenameWithoutExtension = filename.split('.').slice(0, -1).join('.')
  const filenameParts = filenameWithoutExtension.split('_')
  // Remove UUID from filename, if present
  const filteredFilenameParts = filenameParts.filter(
    (filenamePart) => !uuidRegex.test(filenamePart),
  )
  const index = filteredFilenameParts.splice(-1, 1)[0]

  if (index && isNumeric(index)) {
    return Number(index)
  }

  if (index && filenameIndexMap[index]) {
    return filenameIndexMap[index]
  }

  return null
}

/**
 * Gets product gallery photos for selected variant.
 */
export const getProductGalleryPhotos = (
  photosets: SanityProductGalleryPhoto[],
  variant?: SanityProductVariantFragment,
): SanityImageFragment[] => {
  const variantPhotoset = photosets.find(
    (photoset) =>
      !!photoset.forOption &&
      !!variant &&
      matchesForOption(photoset.forOption, variant.options),
  )

  if (variantPhotoset?.photos && variantPhotoset.photos.length > 0) {
    return variantPhotoset.photos
  }

  return photosets.find((photoset) => !photoset.forOption)?.photos ?? []
}

/**
 * Gets product listing thumbnails for selected variant using gallery photos.
 */
export const getProductListingThumbnails = (
  galleryPhotos: SanityProductGalleryPhoto[],
  listingPhotos: SanityProductListingPhoto[] | SanityProductCartPhoto[],
  options: SanityProductVariantOption[],
) => {
  const defaultListingPhoto = listingPhotos?.find((listingPhoto) => {
    return typeof listingPhoto === 'string' || !listingPhoto.forOption
  })
  const variantListingPhoto = listingPhotos?.find((listingPhoto) => {
    return (
      !!listingPhoto.forOption &&
      matchesForOption(listingPhoto.forOption, options)
    )
  })
  const listingPhoto = variantListingPhoto ?? defaultListingPhoto

  const optionGallery = listingPhoto?.forOption
    ? galleryPhotos.find((gallery) => {
        return (
          gallery.forOption?.toLowerCase() ===
          listingPhoto.forOption?.toLowerCase()
        )
      })
    : null
  const defaultGallery = galleryPhotos.find((gallery) => !gallery.forOption)
  const gallery = optionGallery ?? defaultGallery

  const photos = gallery?.photos ?? []

  return photos.find((photo) => photo.asset._id === listingPhoto?.galleryImage)
}

/**
 * Find listing thumbnail in gallery photos.
 */
export const getProductListingThumbnail = (
  galleryPhotos: SanityProductGalleryPhoto[],
  forOption?: string,
  assetId?: string,
) => {
  if (!assetId) {
    return
  }

  // Get the gallery for given option, falling back to the default gallery
  const optionGallery = forOption
    ? galleryPhotos.find((gallery) => {
        return gallery.forOption?.toLowerCase() === forOption.toLowerCase()
      })
    : null
  const defaultGallery = galleryPhotos.find((gallery) => !gallery.forOption)
  const gallery = optionGallery ?? defaultGallery

  const photo = gallery?.photos?.find((photo) => photo.asset._id === assetId)

  return photo
}
